import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router';
import { Field, PasswordField } from '../components/ui.jsx';
import { useAuth, homeFor, IDLE_SIGNOUT_FLAG } from '../state/AuthContext.jsx';
import { useToast } from '../state/ToastContext.jsx';
import { useTitle } from '../lib/hooks.js';

const Login = () => {
  useTitle('Sign in');
  const { signIn, verifyMfa, resendMfa } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();

  const [form, setForm] = useState({ identifier: '', password: '' });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  // Set once /auth/login comes back asking for the emailed code instead of a
  // token; holding the mfaToken here is what lets the second step complete
  // the same sign-in without asking for the password again.
  const [mfa, setMfa] = useState(null);
  const [code, setCode] = useState('');
  const [resending, setResending] = useState(false);
  // Read on first render, cleared after mount - clearing inside the initializer
  // would lose the flag to StrictMode's double render before it was shown.
  const [idleSignedOut] = useState(() => sessionStorage.getItem(IDLE_SIGNOUT_FLAG) === '1');
  useEffect(() => {
    sessionStorage.removeItem(IDLE_SIGNOUT_FLAG);
  }, []);

  const proceed = (user) => {
    toast.success(`Welcome back, ${user.name.split(' ')[0]}.`);
    const next = params.get('next') || location.state?.from?.pathname || homeFor(user);
    navigate(next, { replace: true });
  };

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      const result = await signIn(form.identifier.trim(), form.password);
      if (result.mfaRequired) {
        setMfa({ mfaToken: result.mfaToken, email: result.email });
        return;
      }
      proceed(result);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  };

  const submitCode = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      const user = await verifyMfa(mfa.mfaToken, code.trim());
      proceed(user);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    setResending(true);
    setError('');
    try {
      const mfaToken = await resendMfa(mfa.mfaToken);
      setMfa({ ...mfa, mfaToken });
      toast.success('A new code is on its way.');
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setResending(false);
    }
  };

  if (mfa) {
    return (
      <div className="auth-page container">
        <div className="auth-card">
          <h1>Enter your code</h1>
          <p className="muted">We emailed a 6-digit code to {mfa.email}. It expires in 10 minutes.</p>

          {error ? <div className="notice notice-bad">{error}</div> : null}

          <form className="stack" onSubmit={submitCode}>
            <Field label="Code" required>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
                required
                value={code}
                onChange={(event) => setCode(event.target.value)}
              />
            </Field>

            <button type="submit" className="btn btn-primary btn-block btn-lg" disabled={busy}>
              {busy ? 'Verifying...' : 'Verify and sign in'}
            </button>
          </form>

          <p className="auth-alt">
            Didn't get it?{' '}
            <button type="button" className="link-button" onClick={resend} disabled={resending}>
              {resending ? 'Sending...' : 'Resend code'}
            </button>
          </p>
          <p className="auth-alt">
            <button type="button" className="link-button" onClick={() => { setMfa(null); setCode(''); setError(''); }}>
              Back to sign in
            </button>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page container">
      <div className="auth-card">
        <h1>Sign in</h1>
        <p className="muted">Pick up where you left off with your applications, messages and saved houses.</p>

        {idleSignedOut ? (
          <div className="notice notice-info">You were signed out after 5 minutes of inactivity. Sign in to continue.</div>
        ) : null}
        {error ? <div className="notice notice-bad">{error}</div> : null}

        <form className="stack" onSubmit={submit}>
          <Field label="Email or username" required>
            <input
              type="text"
              autoComplete="username"
              required
              value={form.identifier}
              onChange={(event) => setForm({ ...form, identifier: event.target.value })}
            />
          </Field>

          <PasswordField
            label="Password"
            required
            autoComplete="current-password"
            value={form.password}
            onChange={(event) => setForm({ ...form, password: event.target.value })}
          />

          <button type="submit" className="btn btn-primary btn-block btn-lg" disabled={busy}>
            {busy ? 'Signing in...' : 'Sign in'}
          </button>
        </form>

        <p className="auth-alt">
          No account yet? <Link to="/register">Create one, it takes a minute</Link>
        </p>
        <p className="auth-alt">
          Forgot your password? <Link to="/forgot-password">Reset it here</Link>
        </p>
        {/* Somebody who applied as a guest has no password until a landlord
            approves them, so "wrong password" would be the wrong answer here. */}
        <p className="auth-alt">
          Applied for a house without registering? <Link to="/track">Track your application</Link>.
          Your sign-in details are emailed to you once a landlord approves you.
        </p>
      </div>

      <aside className="auth-aside">
        <h2>Secure access</h2>
        <p className="muted">
          This area is protected. Only authorized accounts can sign in and manage platform operations.
        </p>
        <ul className="demo-list">
          <li>
            <strong>Privacy-first</strong>
            <span>Account details are not displayed publicly.</span>
          </li>
        </ul>
      </aside>
    </div>
  );
};

export default Login;
